/**
 * `POST /v1/orders`, `GET /v1/me/orders[/{id}]` e `POST /v1/me/orders/{id}/cancel` — **o pedido
 * do cliente**.
 *
 * Fontes: `API §6.12` (as linhas de rota), `§6.20.7` (criar), `§6.20.8` (detalhe), `§3`
 * (idempotencia), `CONTRACTS §2.1` (os 12 estados, 8 no MVP), `§2.3` (meios de pagamento), `§6`
 * (a cotacao e os dois codigos canonicos) e `DOMAIN §4.2` (a maquina de estados).
 *
 * ## A decisao que governa a REQUISICAO: o pedido nasce de uma COTAÇÃO
 *
 * `API §6.20.7` chama isso de **forma canonica unica** e nomeia as duas formas eliminadas: o
 * corpo com `items[]` (`SECURITY §8.1`) e o par `{quoteId, quoteHash}` solto do PWA-042 antigo.
 * O corpo nao tem item, nao tem quantidade, nao tem preco unitario, nao tem desconto e nao tem
 * frete. Ele tem um `quoteId`, e o servidor recalcula.
 *
 * ### As duas excecoes monetarias, e por que nenhuma das duas e "preco do cliente"
 *
 * | Campo | O que e | Por que nao vicia o total |
 * | --- | --- | --- |
 * | `acknowledgedTotalCents` | **asserção**: "foi este o total que eu vi" | `API §6.20.7`: *"só pode produzir 409, jamais influenciar o cálculo"*. O servidor compara; divergiu, recusa. Nunca soma, nunca substitui |
 * | `payment.changeForCents` | "troco para R$ 100,00" | e quanto o cliente vai **entregar em dinheiro**, nao quanto o pedido custa. Menor que o total ⇒ `CHANGE_AMOUNT_INVALID` (422, INV-O10) |
 *
 * O `.strict()` recusa qualquer outro campo monetario com `422 VALIDATION_ERROR` — recusado, e
 * nao descartado em silencio, porque "descartado em silencio" e indistinguivel de "aceito e
 * perdido" e so a recusa deixa rastro no teste (SEC-06/SEC-10).
 *
 * ## O que ficou de FORA, e nao por esquecimento
 *
 * - **`prescriptionIds`** — `API §6.20.7` o marca *"previsto (onda O1)"*. Contrato previsto nao
 *   entra no schema do MVP (`CONTRACTS §8`, regra do recorte): com `.strict()`, mandar o campo
 *   e `422`, que e a resposta honesta enquanto `FEATURE_RX_CHECKOUT` nao existe. No MVP a
 *   receita e conferida na entrega, e `requiresPrescription` na cotacao ja avisa o cliente.
 * - **`contractSummary`** com `policyVersion` e `withdrawalRightText` — a mesma DUVIDA que
 *   `checkout.ts` declara: nao ha tabela de politica legal versionada em `CONTRACTS §8.1` nem
 *   chave no `DeploymentProfile`. O que existe e `acceptedPolicyVersion`, que o cliente **ecoa**
 *   da tela; ele e gravado no snapshot do contrato como registro do que foi exibido, e nao como
 *   afirmacao nossa sobre o texto.
 */
import { z } from 'zod';

import {
  CancellationReasonSchema,
  FulfillmentModeSchema,
  OrderChannelSchema,
  OrderStatusSchema,
  PaymentMethodSchema,
  PaymentStatusSchema,
} from '../enums';
import {
  CursorSchema,
  InstantSchema,
  MoneySchema,
  NonNegativeMoneySchema,
  OrderCodeSchema,
  PAGE_LIMIT_MAX_ORDERS,
  PageInfoSchema,
  Uuidv7Schema,
} from '../common';

import { EtaBandSchema } from './checkout';

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

/**
 * `API §6.12`: `GET /v1/me/orders/{id}` e **`PRIV, no-store`** — *"contém item de saúde"*.
 *
 * O mesmo vale para a lista e para a criacao. Nao ha `max-age` positivo em rota de pedido em
 * lugar nenhum: uma borda que guardasse a resposta entregaria o que um cliente comprou ao
 * proximo, e em farmacia isso e dado de saude (REG-044).
 */
export const ORDER_CACHE_CONTROL = 'private, no-store';

/**
 * O SLA de aceite: **20 minutos** (`API §6.20.7`, RN-087, P6).
 *
 * `API §6.20.7` fixa o numero na resposta (`acceptanceSlaExpiresAt`), e `IMPLANTACAO §2` o poe
 * como chave de perfil (`store.acceptSlaSeconds`, default 300 para o destaque e 1200 para o
 * cancelamento). O valor **efetivo** vem da coluna `store.autoCancelSeconds`; esta constante e
 * o default do produto, usado quando a filial nao o sobrescreve.
 */
export const ACCEPTANCE_SLA_MINUTES = 20;

// ---------------------------------------------------------------------------
// Requisicao
// ---------------------------------------------------------------------------

/**
 * O meio de pagamento, discriminado — `CONTRACTS §2.3`.
 *
 * Uniao discriminada e nao objeto com campos opcionais: os ramos exigem coisas diferentes, e um
 * objeto plano permitiria `{ method: 'PIX', changeForCents: 5000 }`, que nao quer dizer nada.
 *
 * `CREDIARIO` e `CARTAO_ONLINE` sao **previstos** (`API §6.13`): estao no enum porque o enum e
 * contrato do produto, e a rota responde `PAYMENT_METHOD_UNAVAILABLE` (422) quando o perfil da
 * instancia nao os publica em `payment.methods`. Deixa-los fora do schema faria o cliente
 * receber `VALIDATION_ERROR`, que diz "voce mandou coisa errada" em vez de "esta farmacia nao
 * aceita isso".
 */
export const OrderPaymentRequestSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('PIX') }).strict(),
  z
    .object({
      method: z.literal('DINHEIRO_ENTREGA'),
      /**
       * Quanto o cliente vai entregar em dinheiro. `null` = "não preciso de troco"
       * (`API §6.20.7`). Menor que o total ⇒ `CHANGE_AMOUNT_INVALID` (INV-O10).
       */
      changeForCents: z.number().int().nonnegative().nullable(),
    })
    .strict(),
  z
    .object({ method: z.literal('MAQUININHA_ENTREGA'), cardKind: z.enum(['DEBIT', 'CREDIT']) })
    .strict(),
  z.object({ method: z.literal('CREDIARIO') }).strict(),
  z.object({ method: z.literal('CARTAO_ONLINE'), gatewayToken: z.string().min(1) }).strict(),
]);
export type OrderPaymentRequest = z.infer<typeof OrderPaymentRequestSchema>;

/**
 * `notes` — **máx. 280 chars, e nunca instrução de dispensação** (`API §6.20.7`).
 *
 * O limite nao e estetico: o campo vai para a tela do balconista e para o ticket termico, e um
 * texto longo empurra a informacao que importa para fora do papel. A frase "nunca usado como
 * instrucao de dispensacao" e regulatoria — quem decide o que sai da prateleira e o
 * farmaceutico (REG-005), nao um campo de texto livre do app.
 */
export const OrderNotesSchema = z.string().max(280);

export const CreateOrderRequestSchema = z
  .object({
    /** A cotacao do servidor. TTL de 10 min (`CONTRACTS §6`); vencida ⇒ `410 QUOTE_EXPIRED`. */
    quoteId: Uuidv7Schema,
    /** O hash **de conteudo** que a cotacao devolveu. Divergiu ⇒ `409 PRICE_CHANGED`. */
    quoteHash: z.string().regex(/^[0-9a-f]{64}$/, 'quoteHash é sha256 em hexadecimal minúsculo.'),
    cartId: Uuidv7Schema,
    /** Ver a tabela no cabecalho: asserção, nunca insumo. So pode produzir `409`. */
    acknowledgedTotalCents: z.number().int().nonnegative(),
    payment: OrderPaymentRequestSchema,
    notes: OrderNotesSchema.optional(),
    /** R-05 — CPF na nota. Chega mascarado ou nao; a normalizacao e do servidor. */
    cpfOnInvoice: z.string().min(11).max(14).optional(),
    /** A versao dos Termos exibida no checkout. Ecoada, gravada no snapshot do contrato. */
    acceptedPolicyVersion: z.string().min(1).max(64),
  })
  .strict();
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

/**
 * `POST /v1/me/orders/{id}/cancel` — o corpo e **so o motivo**, e ele e opcional.
 *
 * O cliente nao escolhe entre os 13 valores de `CancellationReason`: quase todos descrevem
 * decisao da loja ou do sistema (`LOJA_RECUSOU`, `SLA_ACEITE_ESTOURADO`, `RUPTURA_TOTAL`). Do
 * app so cabe `CLIENTE_DESISTIU`, e e o que a maquina de estados de `@farmacia/domain` aplica
 * como `defaultCancellationReason` das transicoes com ator `CUSTOMER`. O campo existe para o
 * **texto livre**, que vai para o relatorio de motivo de cancelamento da farmacia.
 */
export const CancelOrderRequestSchema = z
  .object({
    /** Ate 280 chars, como `notes`. Vai para `order_status_history.reason`. */
    note: z.string().max(280).optional(),
  })
  .strict();
export type CancelOrderRequest = z.infer<typeof CancelOrderRequestSchema>;

/**
 * `GET /v1/me/orders?cursor=&limit=` — `API §1.4`, teto de 50 (`PAGE_LIMIT_MAX_ORDERS`).
 *
 * ## DUVIDA declarada: a `page` numerica ate 5
 *
 * `API §1.4` abre uma **excecao justificada** para esta rota: *"`GET /v1/me/orders` tambem
 * aceita `page` numerica ate a pagina 5, porque a UI de 'meus pedidos' precisa de 'pagina 2'
 * acessivel por link"*. O campo **nao esta aqui**, e a omissao e deliberada e reportada.
 *
 * O motivo: `page` numerica sobre uma colecao ordenada por `createdAt` e exatamente o
 * `offset` que o mesmo `§1.4` descarta tres paragrafos acima (*"duplica e pula item entre
 * paginas"*) — um pedido novo entre a pagina 1 e a 2 empurra todos, e o cliente ve o mesmo
 * pedido duas vezes. A excecao e coerente **se** a UI aceitar essa imprecisao em troca do link
 * profundo, e essa e uma decisao de produto, nao de implementacao.
 *
 * Com `.strict()`, `?page=2` responde `422` hoje — recusa explicita, e nao silencio. Quando a
 * decisao vier, o campo entra aqui e o caso de uso ganha o ramo de `offset` limitado a 5.
 */
export const MyOrdersQuerySchema = z
  .object({
    cursor: CursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX_ORDERS).default(20),
  })
  .strict();
export type MyOrdersQuery = z.infer<typeof MyOrdersQuerySchema>;

// ---------------------------------------------------------------------------
// Resposta
// ---------------------------------------------------------------------------

/** Os totais do pedido — os mesmos nomes de `QuoteTotalsSchema`, porque sao o mesmo numero. */
export const OrderTotalsSchema = z.object({
  subtotal: NonNegativeMoneySchema,
  discounts: NonNegativeMoneySchema,
  deliveryFee: NonNegativeMoneySchema,
  total: NonNegativeMoneySchema,
});
export type OrderTotals = z.infer<typeof OrderTotalsSchema>;

/**
 * O bloco de pagamento. `pix` aparece so quando ha cobranca criada — no MVP ela nasce em
 * `POST /v1/orders/{id}/payments` (`API §6.13`), e nao junto com o pedido.
 */
export const OrderPaymentSchema = z.object({
  id: Uuidv7Schema,
  method: PaymentMethodSchema,
  status: PaymentStatusSchema,
  paidAt: InstantSchema.nullable(),
  changeFor: MoneySchema.nullable(),
});
export type OrderPayment = z.infer<typeof OrderPaymentSchema>;

/**
 * O endereco de entrega **como snapshot** — `X-09`: e coluna de `order`, nao tabela.
 *
 * `zoneLabel` vem junto porque o cliente reconhece o bairro antes de reconhecer a rua, e o
 * balcao usa a zona para achar o entregador.
 */
export const OrderDeliveryAddressSchema = z.object({
  zoneLabel: z.string().min(1),
  street: z.string().min(1),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  reference: z.string().nullable(),
  city: z.string().min(1),
  uf: z.string().length(2),
});
export type OrderDeliveryAddress = z.infer<typeof OrderDeliveryAddressSchema>;

export const OrderFulfillmentSchema = z.object({
  mode: FulfillmentModeSchema,
  /** Presente so em `DELIVERY`. */
  address: OrderDeliveryAddressSchema.nullable(),
  etaBand: EtaBandSchema.nullable(),
  /**
   * **O codigo de retirada nao viaja aqui.** `API §6.14` lhe da rota propria
   * (`GET /v1/me/orders/{id}/pickup-code`, `no-store`), e o banco guarda so o hash
   * (`order.pickupCodeHash`). Um codigo em toda leitura de pedido apareceria em log de
   * navegador, em print de tela e no espelho local do PWA.
   */
  pickupExpiresAt: InstantSchema.nullable(),
  dispatchedAt: InstantSchema.nullable(),
  deliveredAt: InstantSchema.nullable(),
});
export type OrderFulfillment = z.infer<typeof OrderFulfillmentSchema>;

/**
 * `201 Created` de `POST /v1/orders` — `API §6.20.7`, com `Location: /v1/me/orders/{id}`.
 *
 * `status` e `AGUARDANDO_PAGAMENTO` (Pix) ou `AGUARDANDO_ACEITE` (pagar na entrega/retirada), e
 * a escolha e da maquina de estados (`initialStatusFor`, RN-086: *"o compromisso nasce do
 * aceite, nao do pagamento"*). Nao ha `@default` na coluna (X-42).
 */
export const OrderCreatedResponseSchema = z.object({
  id: Uuidv7Schema,
  /** "SOL-4821" — e o que vai no WhatsApp e no ticket termico. O prefixo e por instancia. */
  code: OrderCodeSchema,
  status: OrderStatusSchema,
  /** Base do `ETag` fraco `W/"order-<id>-<version>"` (`API §4.2`). */
  version: z.number().int().nonnegative(),
  storeId: Uuidv7Schema,
  channel: OrderChannelSchema,
  createdAt: InstantSchema,
  totals: OrderTotalsSchema,
  payment: OrderPaymentSchema,
  fulfillment: OrderFulfillmentSchema,
  /** REG-064 — imutavel. No MVP e o hash do snapshot em `order.contractTextHash`. */
  contractSnapshotId: z.string().min(1),
  /** 20 min: estourou, cancela e estorna (`API §6.20.7`, RN-087). */
  acceptanceSlaExpiresAt: InstantSchema,
});
export type OrderCreatedResponse = z.infer<typeof OrderCreatedResponseSchema>;

/**
 * Uma linha da linha do tempo. `actorKind` e **despersonalizado** (`API §6.20.8`): "a farmácia",
 * "o entregador".
 *
 * O nome do funcionario existe em `audit_log` e no Painel, e nao aqui. O cliente nao precisa
 * saber quem separou o pedido dele, e a farmacia nao deveria expor o balconista a uma
 * reclamacao nominal.
 */
export const ORDER_TIMELINE_ACTOR_VALUES = [
  'CLIENTE',
  'FARMACIA',
  'FARMACEUTICO',
  'ENTREGADOR',
  'SISTEMA',
] as const;
export const OrderTimelineActorSchema = z.enum(ORDER_TIMELINE_ACTOR_VALUES);
export type OrderTimelineActor = z.infer<typeof OrderTimelineActorSchema>;

export const OrderTimelineEntrySchema = z.object({
  status: OrderStatusSchema,
  at: InstantSchema,
  actorKind: OrderTimelineActorSchema,
  note: z.string().nullable(),
});
export type OrderTimelineEntry = z.infer<typeof OrderTimelineEntrySchema>;

/**
 * Uma linha do pedido, **do snapshot** (`order_item.*Snap`, `DATA_MODEL §4.3`).
 *
 * O nome exibido e o do momento da compra, e nao o do catalogo de hoje: o read model do ERP e
 * sobrescrito a cada 15 min, e sem o snapshot o pedido de ontem mudaria de nome sozinho.
 */
export const OrderItemSchema = z.object({
  id: Uuidv7Schema,
  productId: Uuidv7Schema,
  displayName: z.string().min(1),
  quantity: z.number().int().positive(),
  /** `null` ate a separacao comecar. */
  pickedQuantity: z.number().int().nonnegative().nullable(),
  unitPrice: NonNegativeMoneySchema,
  lineTotal: NonNegativeMoneySchema,
  requiresPrescription: z.boolean(),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

/** `cancellation.allowed` responde a pergunta que o botao da tela faz (`API §6.20.8`). */
export const OrderCancellationSchema = z.object({
  allowed: z.boolean(),
  /** Por que nao, quando `allowed` e falso. Texto para humanos. */
  reasonIfNot: z.string().nullable(),
  /** Preenchido quando o pedido **esta** cancelado. */
  reason: CancellationReasonSchema.nullable(),
  at: InstantSchema.nullable(),
});
export type OrderCancellation = z.infer<typeof OrderCancellationSchema>;

/**
 * `GET /v1/me/orders/{id}` — `API §6.20.8`, recortado ao que o MVP consegue servir.
 *
 * O que **nao** esta aqui, e por que: `pendingAdjustment` (a rota de ajuste e da onda seguinte),
 * `return` (devolucao e atendida manualmente no MVP — `API §6.12`), `fiscal.danfeUrl`/`xmlUrl`
 * (`fiscal_document` e tabela prevista; o que existe e o `fiscalStatus`, que ja e coluna de
 * `order`) e `substitutedBy`/`shortage` nos itens (nascem com `OrderAdjustment`). Cada um deles
 * entra com a onda que traz a tabela — `CONTRACTS §8`, regra do recorte.
 */
export const OrderDetailSchema = z.object({
  id: Uuidv7Schema,
  code: OrderCodeSchema,
  version: z.number().int().nonnegative(),
  status: OrderStatusSchema,
  storeId: Uuidv7Schema,
  channel: OrderChannelSchema,
  createdAt: InstantSchema,
  timeline: z.array(OrderTimelineEntrySchema),
  items: z.array(OrderItemSchema),
  totals: OrderTotalsSchema,
  payment: OrderPaymentSchema,
  fulfillment: OrderFulfillmentSchema,
  cancellation: OrderCancellationSchema,
  contractSnapshotId: z.string().min(1),
  acceptanceSlaExpiresAt: InstantSchema.nullable(),
  requiresPrescription: z.boolean(),
});
export type OrderDetail = z.infer<typeof OrderDetailSchema>;

/**
 * A linha da lista. **Nao contem item** — e a decisao mais importante deste arquivo depois do
 * corpo da criacao.
 *
 * `GET /v1/me/orders` e a tela de historico, e o nome de um medicamento numa lista de 20 linhas
 * e inferencia de saude em massa: um print, um espelho local do PWA ou um log de proxy passam a
 * carregar o que a pessoa toma. `itemCount` responde a pergunta que a tela faz ("quantos
 * itens?") sem responder a que ela nao deveria fazer.
 */
export const OrderSummarySchema = z.object({
  id: Uuidv7Schema,
  code: OrderCodeSchema,
  status: OrderStatusSchema,
  storeId: Uuidv7Schema,
  createdAt: InstantSchema,
  mode: FulfillmentModeSchema,
  itemCount: z.number().int().positive(),
  total: NonNegativeMoneySchema,
  paymentMethod: PaymentMethodSchema,
  paymentStatus: PaymentStatusSchema,
});
export type OrderSummary = z.infer<typeof OrderSummarySchema>;

export const MyOrdersResponseSchema = z.object({
  data: z.array(OrderSummarySchema),
  page: PageInfoSchema,
});
export type MyOrdersResponse = z.infer<typeof MyOrdersResponseSchema>;
